import { Link as RouterLink } from "react-router-dom";
import { Link, Tooltip, Chip, Box, Typography } from "@mui/material";
import { PriceGroupActionMenu } from "./PriceActionMenu";
import enums from "src/util/enums";
import { Icon } from "@iconify/react";
import priceTagFill from "@iconify/icons-eva/pricetags-fill";
import clockFill from "@iconify/icons-eva/clock-fill";
import { fDisplayDate } from "src/utils/formatTime";

export const priceGroupListColumns = (isReadOnly) => [
  {
    field: "Actions",
    headerName: "",
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ id, row }) => {
      return <PriceGroupActionMenu id={id} row={row} />;
    },
  },
  {
    field: "name",
    headerName: "Price Group Name",
    width: 200,
    renderCell: ({ value, id }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon icon={priceTagFill} width={20} height={20} color="white" />
        </Box>
        <Box>
          {isReadOnly ? (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {value}
            </Typography>
          ) : (
            <Link 
              underline="none" 
              component={RouterLink} 
              to={`${id}`}
              sx={{ 
                fontWeight: 600,
                color: 'primary.main',
                '&:hover': {
                  color: 'primary.dark',
                  textDecoration: 'underline',
                },
                transition: 'all 0.2s ease',
              }}
            >
              {value}
            </Link>
          )}
        </Box>
      </Box>
    ),
  },
  {
    field: "description",
    headerName: "Description",
    flex: 1,
    minWidth: 250,
    renderCell: ({ value }) => (
      <Tooltip title={value} placement="top" arrow>
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'text.secondary',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            maxWidth: '100%',
          }}
        >
          {value || 'No description provided'}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: "priceGroupType",
    headerName: "Pricing Type",
    width: 140,
    renderCell: ({ value }) => {
      // Handle different possible values
      const isFixed = value === 'fixed' || value === 'FIXED' || value === 1 || value === true;
      const isDynamic = value === 'dynamic' || value === 'DYNAMIC' || value === 'variable' || value === 'VARIABLE' || value === 0 || value === false;
      
      let label = 'Unknown';
      let color = 'default';
      
      if (isFixed) {
        label = 'Fixed Pricing';
        color = 'success';
      } else if (isDynamic) {
        label = 'Dynamic Pricing';
        color = 'primary';
      } else {
        // Fallback - show the actual value
        label = value ? String(value) : 'Not Set';
        color = 'default';
      }
      
      return (
        <Chip
          label={label}
          size="small"
          color={color}
          variant="filled"
          sx={{
            fontWeight: 600,
            textTransform: 'capitalize',
            '& .MuiChip-label': {
              fontSize: '0.75rem',
            },
          }}
        />
      );
    },
  },
  {
    field: "created",
    headerName: "Creation Time",
    width: 140,
    sortable: true,
    type: 'dateTime',
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Icon icon={clockFill} width={14} height={14} color="#9e9e9e" />
        <Typography variant="caption" color="text.secondary">
          {value ? fDisplayDate(value) : 'N/A'}
        </Typography>
      </Box>
    ),
  },
  {
    field: "updated",
    headerName: "Updation Time",
    width: 140,
    sortable: true,
    type: 'dateTime',
    renderCell: ({ value }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Icon icon={clockFill} width={14} height={14} color="#9e9e9e" />
        <Typography variant="caption" color="text.secondary">
          {value ? fDisplayDate(value) : 'N/A'}
        </Typography>
      </Box>
    ),
  },
];

export const initialState = {
  SUNDAY: false,
  MONDAY: false,
  TUESDAY: false,
  WEDNESDAY: false,
  THURSDAY: false,
  FRIDAY: false,
  SATURDAY: false,
  isAll: false,
  copy: [!1, !1, !1, !1, !1, !1, !1],
};

export const getPriceGroupDefaultValue = (teamId) => ({
  priceGroupId: "",
  name: "",
  description: "",
  currency: "",
  paymentGateway: "",
  paymentGatewayId: "",
  transactionPercent: "",
  taxPercent: "",
  companyId: teamId,
  priceGroupType: "fixed",
  priceDetails: [
    {
      day: 1,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      // currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 2,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 3,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 4,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 5,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 6,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
    {
      day: 0,
      priceDetailsId: 0,
      priceperMin: "",
      priceperHr: "",
      priceperKW: "",
      fixedFeeStarting: 0,
      fixedFeeCharging: 0,
      minuteFeeCharging: 0,
      idealFeeFullyCharged: 0,
      currency: "INR",
      startTime: "00:00",
      endTime: "23:59",
      priceType: enums.PRICE_PER.KWH,
    },
  ],
});
